import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FieldValues } from 'react-hook-form';
import { useAssessmentsQuestions } from './useAssessmentsQuestions';
import { ASSESSMENTS_PAGE_SIZE } from '../constants';
import {
  selectCurrentPage,
  selectPages,
  selectSkippedQuestionNumbers,
  setActiveQuestionId,
  setCurrentPage,
  setPages,
} from '../assessmentsSlice';
import {
  generatePage,
  generatePageList,
  getAssessmentsPageCount,
} from '../utils/assessmentsPageUtils';
import { useAssessmentsContext } from './useAssessmentsContext';
import { useAssessmentsAnswers } from './useAssessmentsAnswers';
import { useAssessmentsFormChangeDetection } from './useAssessmentsFormChangeDetection';

export const useAssessmentsPages = () => {
  const dispatch = useDispatch();
  const skippedQuestionNumbers = useSelector(selectSkippedQuestionNumbers);
  const currentPage = useSelector(selectCurrentPage);
  const pages = useSelector(selectPages);
  const { questions } = useAssessmentsQuestions();
  const { autoSaveAnswersImmediate } = useAssessmentsAnswers();
  const { shouldAutoSave, resetDirtyState } = useAssessmentsFormChangeDetection();
  const skippedQuestionNumbersRef = useRef(skippedQuestionNumbers);
  const {
    formMethods: { watch, getValues },
  } = useAssessmentsContext();
  const formValues = watch();

  skippedQuestionNumbersRef.current = skippedQuestionNumbers;

  const paginatedQuestions = useMemo(() => {
    const startIndex = (currentPage - 1) * ASSESSMENTS_PAGE_SIZE;
    return questions?.slice(startIndex, startIndex + ASSESSMENTS_PAGE_SIZE) || [];
  }, [questions, currentPage]);

  const pagesCount = useMemo(() => getAssessmentsPageCount(questions ?? []), [questions]);

  const currentPageProgress = useMemo(
    () =>
      generatePage({
        questions: paginatedQuestions,
        formValues,
        skippedQuestionNumbers,
        pageNumber: currentPage,
      }),
    [paginatedQuestions, formValues, skippedQuestionNumbers, currentPage],
  );

  const setPage = useCallback(
    (newPage: number) => {
      if (newPage >= 1 && newPage <= pagesCount) {
        dispatch(setCurrentPage(newPage));
        dispatch(
          setPages(
            generatePageList({
              questions: questions ?? [],
              formValues: getValues(),
              skippedQuestionNumbers: skippedQuestionNumbersRef.current,
            }),
          ),
        );

        // Only auto-save if there are unsaved changes
        if (shouldAutoSave()) {
          const currentValues = getValues();
          autoSaveAnswersImmediate(currentValues, () => {
            // Reset dirty state after successful save
            // Note: resetDirtyState gets fresh values internally to avoid race conditions
            resetDirtyState();
          }).catch((error) => {
            // If save fails, keep dirty state so next navigation attempt will try again
            // eslint-disable-next-line no-console
            console.error('Auto-save failed during page navigation:', error);
          });
        }
      }
    },
    [
      pagesCount,
      dispatch,
      questions,
      getValues,
      shouldAutoSave,
      autoSaveAnswersImmediate,
      resetDirtyState,
    ],
  );

  // Recalculate all pages when skipped questions or form values change to ensure completion marker updates
  const recalculatedPages = useMemo(
    () =>
      generatePageList({
        questions: questions ?? [],
        formValues,
        skippedQuestionNumbers,
      }),
    [questions, formValues, skippedQuestionNumbers],
  );

  // Merge with optimized page data, preferring current page progress
  const finalPages = useMemo(
    () =>
      recalculatedPages.map((page) => {
        if (page.pageNumber === currentPage) {
          return currentPageProgress;
        }
        return page;
      }),
    [recalculatedPages, currentPageProgress, currentPage],
  );

  const resetPageData = useCallback(
    (latestFormValues: FieldValues, latestSkippedQuestionNumbers?: number[]) => {
      dispatch(
        setPages(
          generatePageList({
            questions: questions ?? [],
            formValues: latestFormValues,
            skippedQuestionNumbers:
              latestSkippedQuestionNumbers ?? skippedQuestionNumbersRef.current,
          }),
        ),
      );
    },
    [questions, dispatch],
  );

  // Reset active question to the first question whenever questions change
  useEffect(() => {
    dispatch(setActiveQuestionId(questions?.[0]?.id ?? null));
  }, [questions, dispatch]);

  return {
    paginatedQuestions,
    currentPage,
    pages: finalPages,
    setPage,
    formValues,
    resetPageData,
  };
};
